import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/pro-solid-svg-icons";
import { twMerge } from "tailwind-merge";
import { forwardRef } from "react";
import type React from "react";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import cn from "mcn";

const variantStyles = {
  solid: {
    primary: cn(
      "bg-[var(--primary-default)] text-[var(--primary-foreground)] border border-transparent",
      "hover:bg-[var(--primary-hover)]",
      "focus-visible:bg-[var(--primary-hover)] focus-visible:ring-2 focus-visible:ring-[var(--primary-hover)]/20",
      "active:bg-[var(--primary-click)]",
      "disabled:bg-[var(--primary-disabled)] disabled:text-[var(--primary-on-disabled)]",
    ),
    neutral: cn(
      "bg-[var(--neutral-default)] text-[var(--neutral-foreground)]",
      "hover:bg-[var(--neutral-hover)]",
      "focus-visible:bg-[var(--neutral-hover)] focus-visible:ring-2 focus-visible:ring-[var(--neutral-hover)]/20",
      "active:bg-[var(--neutral-click)]",
      "disabled:bg-[var(--neutral-disabled)] disabled:text-[var(--neutral-on-disabled)]",
    ),
    danger: cn(
      "bg-[var(--danger-default)] text-[var(--primary-foreground)] border border-transparent",
      "hover:bg-[var(--danger-hover)]",
      "focus-visible:bg-[var(--danger-hover)] focus-visible:ring-2 focus-visible:ring-[var(--danger-hover)]/20",
      "active:bg-[var(--danger-click)]",
      "disabled:bg-[var(--danger-disabled)] disabled:text-[var(--danger-on-disabled)]",
    ),
  },
  outline: {
    primary: cn(
      "bg-transparent border border-[var(--primary-default)] text-[var(--primary-default)]",
      "hover:bg-[var(--primary-tonal-hover)] hover:text-[var(--primary-foreground)]",
      "focus-visible:bg-[var(--primary-tonal-hover)] focus-visible:text-[var(--primary-foreground)] focus-visible:ring-2 focus-visible:ring-[var(--primary-tonal-hover)]/20",
      "active:border-[var(--primary-default)] active:bg-[var(--primary-tonal)] active:text-[var(--primary-on-tonal)]",
      "disabled:border-[var(--primary-on-disabled)] disabled:bg-[var(--primary-disabled)] disabled:text-[var(--primary-on-disabled)]",
    ),
    neutral: cn(
      "bg-transparent border border-[var(--neutral-default)] text-[var(--neutral-default)]",
      "hover:bg-[var(--neutral-tonal-hover)] hover:text-[var(--neutral-foreground)]",
      "focus-visible:bg-[var(--neutral-tonal-hover)] focus-visible:text-[var(--neutral-foreground)] focus-visible:ring-2 focus-visible:ring-[var(--neutral-tonal-hover)]/20",
      "active:border-[var(--neutral-default)] active:bg-[var(--neutral-tonal)] active:text-[var(--neutral-on-tonal)]",
      "disabled:border-[var(--neutral-on-disabled)] disabled:bg-[var(--neutral-disabled)] disabled:text-[var(--neutral-on-disabled)]",
    ),
    danger: cn(
      "bg-transparent border border-[var(--danger-default)] text-[var(--danger-default)]",
      "hover:bg-[var(--danger-tonal-hover)] hover:text-[var(--danger-foreground)]",
      "focus-visible:bg-[var(--danger-tonal-hover)] focus-visible:text-[var(--danger-foreground)] focus-visible:ring-2 focus-visible:ring-[var(--danger-tonal-hover)]/20",
      "active:border-[var(--danger-default)] active:bg-[var(--danger-tonal)] active:text-[var(--danger-on-tonal)]",
      "disabled:border-[var(--danger-on-disabled)] disabled:bg-[var(--danger-disabled)] disabled:text-[var(--danger-on-disabled)]",
    ),
  },
  text: {
    primary: cn(
      "bg-transparent text-[var(--primary-default)] underline border border-transparent",
      "hover:bg-[var(--primary-tonal)] hover:text-[var(--primary-on-tonal)]",
      "focus-visible:bg-[var(--primary-tonal)] focus-visible:text-[var(--primary-on-tonal)] focus-visible:border-[var(--primary-default)]",
      "active:bg-transparent active:text-[var(--primary-click)]",
      "disabled:bg-[var(--primary-disabled)] disabled:text-[var(--primary-on-disabled)]",
    ),
    neutral: cn(
      "bg-transparent text-[var(--neutral-default)] underline border border-transparent",
      "hover:bg-[var(--neutral-tonal)] hover:text-[var(--neutral-on-tonal)]",
      "focus-visible:ring-2 focus-visible:ring-[var(--neutral-tonal)]/20",
      "active:bg-transparent active:text-[var(--neutral-click)] active:border-transparent",
      "disabled:bg-[var(--neutral-disabled)] disabled:text-[var(--neutral-on-disabled)]",
    ),
    danger: cn(
      "bg-transparent text-[var(--danger-default)] underline border border-transparent",
      "hover:bg-[var(--danger-tonal)] hover:text-[var(--danger-on-tonal)]",
      "focus-visible:bg-[var(--danger-tonal)] focus-visible:text-[var(--danger-on-tonal)] focus-visible:border-[var(--danger-default)]",
      "active:bg-transparent active:text-[var(--danger-click)]",
      "disabled:bg-[var(--danger-disabled)] disabled:text-[var(--danger-on-disabled)]",
    ),
  },
  tonal: {
    primary: cn(
      "bg-[var(--primary-tonal)] text-[var(--primary-on-tonal)] border border-transparent",
      "hover:bg-[var(--primary-tonal-hover)]",
      "focus-visible:bg-[var(--primary-tonal-hover)] focus-visible:ring-2 focus-visible:ring-[var(--primary-tonal-hover)]/20",
      "active:bg-[var(--primary-default)]",
      "disabled:bg-[var(--primary-disabled)] disabled:text-[var(--primary-on-disabled)]",
    ),
    neutral: cn(
      "bg-[var(--neutral-tonal)] text-[var(--neutral-on-tonal)] border border-transparent",
      "hover:bg-[var(--neutral-tonal-hover)]",
      "focus-visible:bg-[var(--neutral-tonal-hover)] focus-visible:ring-2 focus-visible:ring-[var(--neutral-tonal-hover)]/20",
      "active:bg-[var(--neutral-default)]",
      "disabled:bg-[var(--neutral-disabled)] disabled:text-[var(--neutral-on-disabled)]",
    ),
    danger: cn(
      "bg-[var(--danger-tonal)] text-[var(--danger-on-tonal)] border border-transparent",
      "hover:bg-[var(--danger-tonal-hover)]",
      "focus-visible:bg-[var(--danger-tonal-hover)] focus-visible:ring-2 focus-visible:ring-[var(--danger-tonal-hover)]/20",
      "active:bg-[var(--danger-default)]",
      "disabled:bg-[var(--danger-disabled)] disabled:text-[var(--danger-on-disabled)]",
    ),
  },
  outlineTonal: {
    primary: cn(
      "bg-transparent border border-[var(--primary-default)] text-[var(--primary-default)]",
      "hover:bg-[var(--primary-tonal)]",
      "focus-visible:ring-2 focus-visible:ring-[var(--primary-tonal)]/20",
      "disabled:border-[var(--primary-on-disabled)] disabled:bg-transparent disabled:text-[var(--primary-on-disabled)]",
    ),
    neutral: cn(
      "bg-transparent border border-[var(--neutral-default)] text-[var(--neutral-default)]",
      "hover:bg-[var(--neutral-tonal)]",
      "focus-visible:ring-2 focus-visible:ring-[var(--neutral-tonal)]/20",
      "disabled:border-[var(--neutral-on-disabled)] disabled:bg-transparent disabled:text-[var(--neutral-on-disabled)]",
    ),
    danger: cn(
      "bg-transparent border border-[var(--danger-default)] text-[var(--danger-default)]",
      "hover:bg-[var(--danger-tonal)]",
      "focus-visible:ring-2 focus-visible:ring-[var(--danger-tonal)]/20",
      "disabled:border-[var(--danger-on-disabled)] disabled:bg-transparent disabled:text-[var(--danger-on-disabled)]",
    ),
  },
  outlineDropdown: {
    neutral: cn(
      "bg-transparent border border-[var(--neutral-default)] text-foreground",
      "hover:bg-[var(--surface-2)]",
      "focus-visible:ring-2 focus-visible:ring-[var(--neutral-tonal)]/20",
      "disabled:border-[var(--neutral-on-disabled)] disabled:bg-transparent disabled:text-[var(--neutral-on-disabled)]",
    ),
    active: cn(
      "bg-transparent border border-[var(--primary-default)] text-foreground",
      "hover:bg-[var(--surface-2)]",
      "focus-visible:ring-2 focus-visible:ring-[var(--neutral-tonal)]/20",
      "disabled:border-[var(--primary-on-disabled)] disabled:bg-transparent disabled:text-[var(--primary-on-disabled)]",
    ),
  },
};

const sizeStyles = {
  sm: "h-[27px] px-[15px] text-[length:var(--font-size-base)] font-medium",
  md: "h-[27px] px-[15px] text-[length:var(--font-size-base)] font-medium",
  lg: "h-[27px] px-[15px] text-[length:var(--font-size-base)] font-medium",
  iconOnly: "h-[27px] w-[27px] min-w-[27px] p-0",
};

const tertiarySizeStyles = {
  sm: "h-[27px] px-[9px] text-[length:var(--font-size-base)] font-medium",
  md: "h-[27px] px-[9px] text-[length:var(--font-size-base)] font-medium",
  lg: "h-[27px] px-[9px] text-[length:var(--font-size-base)] font-medium",
  iconOnly: "h-[27px] w-[27px] min-w-[27px] p-0",
};

const filterDropdownSizeStyles =
  "h-[27px] pl-[9px] pr-[6px] py-[4.5px] text-[length:var(--font-size-base)] font-normal leading-[18px]";

const buttonTypes = {
  save: cn(variantStyles.solid.primary, sizeStyles.md),
  cancel: cn(variantStyles.outline.neutral, sizeStyles.md),
  closePrimary: cn(variantStyles.solid.neutral, sizeStyles.md),
  closeSecondary: cn(variantStyles.outline.neutral, sizeStyles.md),
  linkButton: cn(variantStyles.text.neutral, tertiarySizeStyles.md),
  deletePrimary: cn(variantStyles.solid.danger, sizeStyles.md),
  deleteSecondary: cn(variantStyles.outline.danger, sizeStyles.md),
  deleteTertiary: cn(variantStyles.text.danger, tertiarySizeStyles.md),
  navIcon: cn(
    "bg-transparent p-2",
    "text-[#222222] dark:text-[rgba(255,255,255,0.5)]",
    "hover:text-[var(--color-brand)] hover:bg-transparent",
    "disabled:text-[rgba(0,0,0,0.26)] dark:disabled:text-[rgba(255,255,255,0.3)]",
  ),
  filterActive: cn(variantStyles.outlineTonal.primary, sizeStyles.md),
  filter: cn(variantStyles.outlineTonal.neutral, sizeStyles.md),
  filterDropdownActive: cn(variantStyles.outlineDropdown.active, filterDropdownSizeStyles),
  filterDropdown: cn(variantStyles.outlineDropdown.neutral, filterDropdownSizeStyles),
};

// Legacy colors for backwards compatibility
const legacyColors = {
  neutral: cn(
    "bg-neutral text-white hover:bg-neutral-hover active:bg-neutral-click border border-transparent focus-visible:border-neutral focus-visible:active:border-neutral",
    "px-6 py-1.5 text-base font-medium",
  ),
  primary: cn(
    "bg-primary text-white hover:bg-primary-hover active:bg-primary-click border border-transparent focus-visible:border-primary focus-visible:active:border-primary",
    "disabled:bg-primary-disabled disabled:text-button-disabled-text",
    "px-6 py-1.5 text-base font-medium",
  ),
  danger: cn(
    "bg-danger text-white hover:bg-danger-hover active:bg-danger-click border border-transparent focus-visible:border-danger focus-visible:active:border-danger",
    "px-6 py-1.5 text-base font-medium",
  ),
  warning: cn(
    "bg-warning text-white hover:bg-warning-hover active:bg-warning-click border border-transparent focus-visible:border-warning focus-visible:active:border-warning",
    "px-6 py-1.5 text-base font-medium",
  ),
  icon: cn(
    "bg-background border",
    "hover:bg-neutral-hover",
    'data-[active="true"]:bg-neutral-click',
    "disabled:bg-card-muted disabled:border-card-muted disabled:text-outline",
    "px-6 py-1.5 text-base font-medium",
  ),
  outline: cn(
    "bg-transparent border-1 border-neutral text-foreground",
    "disabled:bg-transparent disabled:border-card-muted disabled:text-outline",
    "px-6 py-1.5 text-base font-medium",
  ),
};

export type ButtonVariant = "solid" | "outline" | "text" | "tonal" | "outlineTonal";
export type ButtonColor = "primary" | "neutral" | "danger";
export type ButtonSize = "sm" | "md" | "lg" | "iconOnly";

export interface ButtonProps extends Omit<React.ComponentProps<"button">, "color"> {
  variant?: ButtonVariant | "icon"; // "icon" is legacy variant
  color?: ButtonColor | keyof typeof legacyColors;
  size?: ButtonSize;
  icon?: IconDefinition;
  iconPosition?: "left" | "right";
  buttonType?: keyof typeof buttonTypes;
  iconWidth?: number;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(allProps, ref) {
  const {
    variant = "solid",
    color = "primary",
    size = "md",
    icon,
    iconPosition = "left",
    buttonType,
    iconWidth,
    children,
    className,
    ...props
  } = allProps;

  const iconWidthStyle =
    size === "iconOnly" && iconWidth ? { width: `${iconWidth}px`, minWidth: `${iconWidth}px` } : undefined;

  if (buttonType) {
    const legacyClass = buttonTypes[buttonType];
    const iconElement = icon ? (
      <FontAwesomeIcon icon={icon} className={children ? (iconPosition === "left" ? "mr-2" : "ml-2") : ""} />
    ) : null;

    return (
      <button
        ref={ref}
        type="button"
        {...props}
        className={twMerge(
          legacyClass,
          "rounded-lg cursor-pointer disabled:cursor-not-allowed focus:outline-none transition-colors duration-150",
          "flex items-center justify-center text-center",
          className,
        )}
      >
        {iconPosition === "left" && iconElement}
        {children}
        {iconPosition === "right" && iconElement}
      </button>
    );
  }

  // Handle legacy "icon" variant (used as both variant and color)
  const isLegacyVariant = variant === "icon";
  const supportsModernVariant =
    !isLegacyVariant && Boolean(variantStyles[variant as ButtonVariant]?.[color as ButtonColor]);
  const shouldUseVariantStyles = supportsModernVariant;
  const isLegacyColor = !shouldUseVariantStyles && color && color in legacyColors;

  const legacyClass = isLegacyColor ? legacyColors[color as keyof typeof legacyColors] : "";
  const legacyVariantClass = isLegacyVariant ? "px-4 py-1.5 rounded-md" : "";
  const isTertiary = variant === "text";
  const variantClass =
    shouldUseVariantStyles && variant && color
      ? cn(
          variantStyles[variant as ButtonVariant]?.[color as ButtonColor],
          isTertiary ? tertiarySizeStyles[size] : sizeStyles[size],
        )
      : "";

  const iconElement = icon ? (
    <FontAwesomeIcon icon={icon} className={children ? (iconPosition === "left" ? "mr-2" : "ml-2") : ""} />
  ) : null;

  return (
    <button
      ref={ref}
      type="button"
      {...props}
      style={{ ...iconWidthStyle, ...props.style }}
      className={twMerge(
        legacyClass,
        legacyVariantClass,
        variantClass,
        "rounded-lg cursor-pointer disabled:cursor-not-allowed focus:outline-none transition-colors duration-150",
        "flex items-center justify-center text-center",
        className,
      )}
    >
      {iconPosition === "left" && iconElement}
      {children}
      {iconPosition === "right" && iconElement}
    </button>
  );
});

const addButtonTailwindClasses =
  "w-7 h-7 bg-neutral/90 border-transparent hover:bg-neutral rounded-sm flex items-center justify-center text-white p-0.5";
export const AddButton = ({ className, ...props }: React.ComponentProps<"button">) => {
  return (
    <Button
      {...props}
      color="icon"
      className={
        // TODO: is this still even used? it seems to be quite slow even when p.className is undefined
        className !== undefined ? twMerge(addButtonTailwindClasses, className) : addButtonTailwindClasses
      }
    >
      <FontAwesomeIcon icon={faPlus} />
    </Button>
  );
};

export const NavIconButton = ({ className, children, ...props }: Omit<ButtonProps, "buttonType">) => {
  return (
    <Button {...props} buttonType="navIcon" className={twMerge("rounded-[4px]", className)}>
      {children}
    </Button>
  );
};

export interface IconButtonProps extends Omit<React.ComponentProps<"button">, "children"> {
  children: React.ReactNode;
  selected?: boolean;
  variant?: "neutral" | "primary";
}

const iconButtonVariantStyles = {
  neutral: [
    "text-[var(--neutral-default)]",
    "hover:bg-[var(--neutral-tonal)] hover:text-[var(--neutral-on-tonal)]",
    "focus-visible:bg-[var(--neutral-tonal)] focus-visible:text-[var(--neutral-on-tonal)] focus-visible:border-[var(--neutral-default)]",
    "active:bg-[var(--neutral-tonal)] active:text-[var(--neutral-on-tonal)] active:border-[var(--neutral-default)]",
    "data-[selected=true]:bg-[var(--neutral-tonal)] data-[selected=true]:text-[var(--neutral-on-tonal)] data-[selected=true]:border-[var(--neutral-default)]",
  ],
  primary: [
    "text-[var(--primary-default)]",
    "hover:bg-[var(--primary-tonal)] hover:text-[var(--primary-on-tonal)]",
    "focus-visible:bg-[var(--primary-tonal)] focus-visible:text-[var(--primary-on-tonal)] focus-visible:border-[var(--primary-default)]",
    "active:bg-[var(--primary-tonal)] active:text-[var(--primary-on-tonal)] active:border-[var(--primary-default)]",
    "data-[selected=true]:bg-[var(--primary-tonal)] data-[selected=true]:text-[var(--primary-on-tonal)] data-[selected=true]:border-[var(--primary-default)]",
  ],
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { className, children, selected, variant = "neutral", ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      {...props}
      data-selected={selected ? "true" : undefined}
      className={twMerge(
        "cursor-pointer rounded-[var(--border-radius-rounded-lg,6px)] p-[var(--spacing-scale-small-150,4.5px)]",
        "border border-transparent",
        "max-h-[27px] min-h-[24px] aspect-square",
        ...iconButtonVariantStyles[variant],
        "disabled:bg-[var(--neutral-disabled)] disabled:text-[var(--neutral-on-disabled)] disabled:cursor-not-allowed",
        "transition-colors duration-150",
        "flex items-center justify-center",
        className,
      )}
    >
      {children}
    </button>
  );
});
